import { useCallback, useState } from 'react';
import { benefitCents, benefitPercent } from '@features/patient-coverages/utils/patientCost';
import { CheckCircle, Close, OpenInNew, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { AdminEligibilityItemResponse, ManualUpdateRequest } from '@store/generated/admin';
import { useSnackbar } from 'notistack';

import { useAdminLogEligibilityVerificationMutation } from '../store/hooks';
import { formatRelativeTime } from '../utils/formatting';

interface EligibilityOverrideFormProps {
  item: AdminEligibilityItemResponse;
  onCollapse: () => void;
}

const STATUS_OPTIONS = [
  { value: 'blocked', label: 'Blocked', color: 'error' as const },
  { value: 'needs_review', label: 'Needs review', color: 'warning' as const },
  { value: 'eligible', label: 'Eligible', color: 'success' as const },
];

const METHOD_OPTIONS = [
  { value: 'portal', label: 'Portal' },
  { value: 'phone', label: 'Phone' },
];

/** Convert cents (from API) to dollars for display. Returns '' for null/undefined. */
const centsToDollars = (cents: number | null | undefined): string | number => {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
};

/** Convert dollars (from form input) to cents for API. Returns null for empty. */
const dollarsToCents = (dollars: string | number): number | null => {
  if (dollars === '' || dollars === null || dollars === undefined) return null;
  return Math.round(parseFloat(String(dollars)) * 100);
};

export function EligibilityOverrideForm({ item, onCollapse }: EligibilityOverrideFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [logVerification, { isLoading }] = useAdminLogEligibilityVerificationMutation();
  const snapshotFinancials: Record<string, unknown> = item.financials ?? {};

  const [formState, setFormState] = useState({
    status: item.status || 'needs_review',
    method: '',
    summary: item.summary || '',
    notes: '',
    copayment: centsToDollars(benefitCents(snapshotFinancials.copayment_cents)),
    coinsurance: benefitPercent(snapshotFinancials) ?? '',
    deductible: centsToDollars(benefitCents(snapshotFinancials.deductible_cents)),
    remaining_deductible: centsToDollars(
      benefitCents(snapshotFinancials.remaining_deductible_cents),
    ),
    oop_max: centsToDollars(benefitCents(snapshotFinancials.oop_max_cents)),
    remaining_oop_max: centsToDollars(benefitCents(snapshotFinancials.remaining_oop_max_cents)),
  });

  const updateField = useCallback((field: string, value: string | number) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  const buildBody = (markResolved: boolean): ManualUpdateRequest => {
    const body: ManualUpdateRequest = {};
    const financials: NonNullable<ManualUpdateRequest['financials']> = {};

    if (formState.status) body.status = formState.status;
    if (formState.method === 'phone' || formState.method === 'portal')
      body.method = formState.method;
    if (formState.summary) body.summary = formState.summary;
    if (formState.notes) body.notes = formState.notes;

    // Financial fields — convert dollars back to cents for API
    const centFields = [
      'copayment',
      'deductible',
      'remaining_deductible',
      'oop_max',
      'remaining_oop_max',
    ] as const;
    for (const field of centFields) {
      const cents = dollarsToCents(formState[field]);
      if (cents !== null) financials[`${field}_cents`] = cents;
    }

    // Coinsurance is a percentage, not cents
    if (
      formState.coinsurance !== '' &&
      formState.coinsurance !== null &&
      formState.coinsurance !== undefined
    ) {
      financials.coinsurance_percent =
        typeof formState.coinsurance === 'string'
          ? parseFloat(formState.coinsurance)
          : formState.coinsurance;
    }

    if (Object.keys(financials).length > 0) body.financials = financials;

    if (markResolved) {
      body.status = 'eligible';
      body.next_action = 'none';
      body.next_action_owner = 'provider';
    }

    return body;
  };

  const handleSave = async (markResolved: boolean) => {
    if (!item.eligibility_snapshot_id) {
      enqueueSnackbar('This eligibility result cannot be updated because it has no snapshot ID.', {
        variant: 'error',
      });
      return;
    }
    try {
      await logVerification({
        eligibilityId: item.eligibility_snapshot_id,
        manualUpdateRequest: buildBody(markResolved),
      }).unwrap();
      enqueueSnackbar(markResolved ? 'Case marked as resolved' : 'Draft saved', {
        variant: 'success',
      });
      if (markResolved) onCollapse();
    } catch {
      enqueueSnackbar('Failed to save override', { variant: 'error' });
    }
  };

  const patientUrl = item.patient_id ? `/app/patient/${item.patient_id}` : undefined;

  return (
    <Box className="bg-white rounded-lg shadow mt-2 mb-4 border border-gray-200">
      {/* Context bar */}
      <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-700 flex-wrap">
          <span className="font-semibold">{item.patient_name || item.patient_id}</span>
          {item.payer_name && <span>{item.payer_name}</span>}
          {item.encounter_date && <span>Encounter: {formatRelativeTime(item.encounter_date)}</span>}
          {item.organization_name && (
            <span className="text-gray-500">{item.organization_name}</span>
          )}
          {patientUrl && (
            <Button
              size="small"
              variant="text"
              endIcon={<OpenInNew fontSize="small" />}
              href={patientUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Patient
            </Button>
          )}
        </div>
        <Button size="small" onClick={onCollapse} startIcon={<Close fontSize="small" />}>
          Close
        </Button>
      </div>

      {/* Alerts */}
      {item.alerts && item.alerts.length > 0 && (
        <div className="px-6 py-2 border-b border-gray-100 flex gap-2 flex-wrap">
          {item.alerts.map((alert, i) => (
            <Chip
              key={i}
              label={alert
                .split('_')
                .map((w, j) =>
                  j === 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase(),
                )
                .join(' ')}
              size="small"
              color="warning"
              variant="outlined"
            />
          ))}
        </div>
      )}

      {/* Form body */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column — Verification */}
          <div className="space-y-4">
            <Typography variant="subtitle2" className="text-gray-500 uppercase tracking-wider">
              Verification
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formState.status}
                label="Status"
                onChange={e => updateField('status', e.target.value)}
              >
                {STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Chip label={opt.label} size="small" color={opt.color} sx={{ minWidth: 80 }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Verification Method</InputLabel>
              <Select
                value={formState.method}
                label="Verification Method"
                onChange={e => updateField('method', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select...</em>
                </MenuItem>
                {METHOD_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Summary"
              value={formState.summary}
              onChange={e => updateField('summary', e.target.value)}
              multiline
              minRows={2}
              maxRows={4}
            />

            <TextField
              fullWidth
              size="small"
              label="Notes"
              value={formState.notes}
              onChange={e => updateField('notes', e.target.value)}
              multiline
              minRows={2}
              maxRows={4}
              placeholder="Internal notes about this case..."
            />
          </div>

          {/* Right column — Financial Details */}
          <div className="space-y-4">
            <Typography variant="subtitle2" className="text-gray-500 uppercase tracking-wider">
              Financial Details
            </Typography>

            {[
              { field: 'copayment', label: 'Copayment', prefix: '$' },
              { field: 'coinsurance', label: 'Coinsurance', prefix: '%' },
              { field: 'deductible', label: 'Deductible', prefix: '$' },
              { field: 'remaining_deductible', label: 'Remaining Deductible', prefix: '$' },
              { field: 'oop_max', label: 'OOP Max', prefix: '$' },
              { field: 'remaining_oop_max', label: 'Remaining OOP', prefix: '$' },
            ].map(({ field, label, prefix }) => (
              <TextField
                key={field}
                fullWidth
                size="small"
                label={label}
                type="number"
                value={formState[field as keyof typeof formState]}
                onChange={e => updateField(field, e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">{prefix}</InputAdornment>,
                  },
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50 rounded-b-lg">
        {isLoading && <CircularProgress size={20} />}
        <Button
          variant="outlined"
          startIcon={<Save />}
          disabled={isLoading || !item.eligibility_snapshot_id}
          onClick={() => void handleSave(false)}
        >
          Save Draft
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
          disabled={isLoading || !item.eligibility_snapshot_id}
          onClick={() => void handleSave(true)}
        >
          Mark Resolved
        </Button>
      </div>
    </Box>
  );
}
